import { PropsWithChildren, useEffect, useState } from 'react';
import { ApolloProvider } from '@apollo/client';
import { handleFirstLoad } from 'src/database/handleFirstLoad.ts';
import { handleRegularLoad } from 'src/database/handleRegularLoad.ts';
import { UserSettings } from 'src/types/UserSettings.ts';
import { DatabaseProviderState } from 'src/database/types.ts';
import { SkeletonMainLayout } from 'src/modules/common/skeletons/SkeletonMainLayout/SkeletonMainLayout.tsx';
import { handleRegularLoadFail } from 'src/database/handleRegularLoadFail.ts';
import { ErrorModal } from 'src/modules/common/components/ErrorModal/ErrorModal.tsx';
import { captureException } from '@sentry/browser';

type Props = PropsWithChildren<{
    userSettings: UserSettings;
    uri: string;
    databaseCache: LocalForage;
    offlineDatabase: LocalForage;
    assetDomain: string;
}>

export const DatabaseProvider = ({
    offlineDatabase,
    databaseCache,
    uri,
    userSettings,
    assetDomain,
    children,
}: Props) => {
    const [state, setState] = useState<DatabaseProviderState | null>(null);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        handleFirstLoad(
            offlineDatabase,
            databaseCache,
            userSettings,
            uri,
            assetDomain,
        ).then((result) => {
            if (result !== false) {
                setState(result);
            } else {
                handleRegularLoad(
                    databaseCache,
                    userSettings,
                    uri,
                    (r: DatabaseProviderState) => {
                        setState(r);
                    },
                    assetDomain,
                ).catch((err) => {
                    // eslint-disable-next-line no-console
                    handleRegularLoadFail(offlineDatabase, databaseCache).catch((e) => console.error(e));
                    captureException(err);
                    // eslint-disable-next-line no-console
                    console.error(err);
                    setError(err);
                });
            }
        }).catch((err) => {
            captureException(err);
            // eslint-disable-next-line no-console
            console.error(err);
            setError(err);
        });
    }, [offlineDatabase, databaseCache, userSettings, uri, assetDomain]);

    if (error !== null) {
        return (
            <SkeletonMainLayout idle={true}>
                <div className="vss-error-boundary">
                    <ErrorModal/>
                </div>
            </SkeletonMainLayout>
        );
    }

    if (state === null) {
        return <SkeletonMainLayout breadcrumbs={false}/>;
    }
    return (
        <ApolloProvider client={state.apollo}>
            {children}
        </ApolloProvider>
    );
};
